import { ToolConnectivityRequestConfig, useStorytellClient } from "@storytell/client";
import { useQuery } from "@tanstack/solid-query";
import { isServer } from "solid-js/web";
import type { IntegrationProviderId } from "~/lib/integrations/integrationProviders";
import { useWire } from "~/wire";
import { authTokenReady } from "~/wire/authTokenReady";

export function useToolConnectivity(projectId?: () => string | undefined) {
  const wire = useWire();
  const client = useStorytellClient();
  const project = () => (projectId ? projectId() : wire.currentProjectId());

  const query = useQuery(() => ({
    queryKey: ["tool-connectivity", project()],
    enabled: !isServer && authTokenReady(wire.auth) && !!project(),
    staleTime: 60_000,
    queryFn: async () => {
      const res = await client.fetch(
        ToolConnectivityRequestConfig.withBearerToken(wire.auth.token()),
        undefined,
        undefined,
        {
          projectIdOverride: project(),
        }
      );
      if (res.isErr()) throw res.error;
      return res.value;
    },
  }));

  // Reading data suspends the nearest boundary whenever the query has none, and the prompt bar's
  // nearest boundary is the app layout's: a failed fetch refetched on window focus swapped the
  // whole chat for a loader, and it came back scrolled to the top. Until an answer arrives every
  // provider reads as not connected; a failed refresh keeps the last answer.
  const integrations = () => (query.isSuccess || query.isRefetchError ? query.data.integrations : undefined);

  return {
    isConnected: (provider: IntegrationProviderId) => !!integrations()?.[provider]?.connected,
    // Whether the current user granted their own account (Slack only). Distinct
    // from isConnected, which reports the org connection.
    hasUserGrant: (integrationKey: string) => !!integrations()?.[integrationKey]?.userConnected,
    isLoading: () => query.isLoading,
    isLoaded: () => query.isSuccess,
  };
}
